/**
 * Lift2Fit — the admin portal's chat button.
 *
 * Every other shell reaches its chat from a chat glyph in the header (a coach
 * and a lifter both open `#/chat` there), so the operator gets the same shape:
 * a 40×40 chat button beside the theme toggle, carrying the unread badge, and it
 * opens the platform's chat interface in its OWN WINDOW like every other chat
 * entry (`target="_blank"`), so the founder can keep the portal open while
 * answering. The surface behind it is the queue of every account's Help
 * thread, not the customer chat workspace: the operator is the other side of
 * that conversation.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/format';
import { ChatIcon } from '../chat';

/** Same cadence as the customer chat's badge; the tab must be visible to poll. */
const SUPPORT_POLL_MS = 10_000;

function useSupportUnread(): number {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const result = await api.adminSupportUnread();
      if (alive && typeof result.data === 'number') setUnread(result.data);
    };
    void tick();
    const timer = window.setInterval(() => void tick(), SUPPORT_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);
  return unread;
}

export function AdminChatButton({ className }: { className?: string }) {
  const unread = useSupportUnread();
  const label =
    unread > 0
      ? `Open Help — ${unread} unread`
      : 'Open Help — the platform chat inbox';

  return (
    <a
      href="#/admin/chat"
      target="_blank"
      rel="noopener"
      data-testid="admin-chat-button"
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-card border border-hairline',
        'text-ink transition-colors duration-150 hover:border-accent hover:text-accent',
        className,
      )}
    >
      <ChatIcon className="h-[18px] w-[18px]" />
      {unread > 0 ? (
        <span
          data-testid="admin-chat-button-badge"
          className="l2f-tabular absolute -right-1 -top-1 min-w-[16px] rounded-pill bg-accent px-1 text-center font-mono text-[9px] font-bold leading-4 text-on-accent"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </a>
  );
}
